import { IScoreRule, ScoreGet, ScoreParameterSheet } from '../interfaces/Score';
import { sortByAttribute } from './sort';

export const groupScoreBySheetParameter = (score: ScoreGet) => {
    const scoreGroupByParams: ScoreParameterSheet[] = [];

    if (score.score_parameters)
        score.score_parameters.forEach(parameter => {
            const foundParam = scoreGroupByParams.find(param => param.sheet_parameter_id === parameter.sheet_parameter_id);
            if (!foundParam) {
                scoreGroupByParams.push({
                    parameters: [parameter],
                    sheet_parameter_id: parameter.sheet_parameter_id,
                    sheet_parameter_option_id: parameter.sheet_parameter_option_id,
                    sheet_parameter: parameter.sheet_parameter
                });
            }
            else {
                const parameters = [...foundParam.parameters, parameter].sort((a, b) => a.puntuation > b.puntuation ? 1 : -1);
                foundParam.parameters = parameters;
            }
        });

    return scoreGroupByParams;
};

export const getPuntuationsFromScore = (scoreParameters: ScoreParameterSheet[]) => {
    const puntuations: number[] = [];
    scoreParameters.forEach(score_param => score_param.parameters.forEach(param => {
        if(!puntuations.includes(param.puntuation)) puntuations.push(param.puntuation);
    }));
    return puntuations.sort((a, b) => a - b);
};

const checkCharsInCommon = (s1: string, s2: string) => {
    for (let i = 0; i < s1.length; i++)
        if (s2.includes(s1[i])) return true;

    for (let j = 0; j < s2.length; j++)
        if (s1.includes(s2[j])) return true;

    return false;
};

export const validateScoreRules = (rules: IScoreRule[]) => {
    
    const colors = new Set();
    const colorRepeated: IScoreRule['id'][] = [];
    const rulesNoValue: IScoreRule['id'][] = [];
    rules.forEach(rule => {
        if (Number.isNaN(rule.value)) rulesNoValue.push(rule.id);
        if (colors.has(rule.color_alert)) colorRepeated.push(rule.id);
        else colors.add(rule.color_alert);
    });
    const repeated: IScoreRule['id'][] = [];
    const incompatibilities: IScoreRule['id'][] = [];
    const sortedRules = [...rules].sort((a, b) => (a.value || 0) > (b.value || 0) ? 1 : -1);
    sortedRules.forEach((rule, index) => {
        const ruleRepeated = sortedRules.find(r =>
            r.id !== rule.id && r.value === rule.value && checkCharsInCommon(r.comparison, rule.comparison));
        if (ruleRepeated) repeated.push(rule.id);
        if (sortedRules[index + 1] !== undefined) {
            const nextRule = sortedRules[index + 1];
            if (rule.comparison.includes('>') && nextRule.comparison.includes('<') && rule.value !== nextRule.value) {
                incompatibilities.push(rule.id);
                incompatibilities.push(nextRule.id);
            }
        }
    });

    if (rulesNoValue.length === 0 && colorRepeated.length === 0 && repeated.length === 0 && incompatibilities.length === 0)
        return [];

    const getErrorScoreRule = (ruleID: IScoreRule['id']) => {
        if (rulesNoValue.some(id => id === ruleID)) return 'required_value';
        if (colorRepeated.some(id => id === ruleID)) return 'rule_with_color_exists';
        if (repeated.some(id => id === ruleID)) return 'rule_incompatible';
        if (incompatibilities.some(id => id === ruleID)) return 'rule_incompatible';
    };

    const errorsRules = rules.map(rule => ({
        id: rule.id,
        error: getErrorScoreRule(rule.id)
    }));

    return errorsRules;

};

export function getHueFromHex(hex: string | null) {
    if (hex === null) return -1;
    const hexLC = hex.toLowerCase();

    // Convert hex to RGB first
    let rHex = '', gHex = '', bHex = '';
    if (hexLC.length === 4) {
        rHex = '0x' + hexLC[1] + hexLC[1];
        gHex = '0x' + hexLC[2] + hexLC[2];
        bHex = '0x' + hexLC[3] + hexLC[3];
    } else if (hexLC.length === 7) {
        rHex = '0x' + hexLC[1] + hexLC[2];
        gHex = '0x' + hexLC[3] + hexLC[4];
        bHex = '0x' + hexLC[5] + hexLC[6];
    }
    // Then get Hue
    const r = Number(rHex) / 255;
    const g = Number(gHex) / 255;
    const b = Number(bHex) / 255;
    const cmin = Math.min(r, g, b),
        cmax = Math.max(r, g, b),
        delta = cmax - cmin;

    let h = 0;

    if (delta === 0) h = 0;
    else if (cmax === r) h = ((g - b) / delta) % 6;
    else if (cmax === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h = Math.round(h * 60);

    if (h < 0) h += 360;


    return h;
}


const doesRuleMatch = (rule: IScoreRule, value: number) => {
    if (rule.value === null || rule.comparison === null) return false;
    if (rule.comparison === '>') return value > rule.value;
    if (rule.comparison === '<') return value < rule.value;
    if (rule.comparison === '=') return value === rule.value;
    if (rule.comparison === '>=') return value >= rule.value;
    if (rule.comparison === '<=') return value <= rule.value;
    return false;
};

const getMostRestrictiveRule = (rules: IScoreRule[], value: number) => {
    let mostRestrictive: IScoreRule | null = null;
    for (let i = 0; i < rules.length; i++) {
        const r = rules[i];
        if (!doesRuleMatch(r, value) || r.value === null) continue;
        else if (r.comparison === '=') return r;
        else if ((r.comparison === '<' || r.comparison === '<=') &&
            (mostRestrictive === null || mostRestrictive.value === null || r.value < mostRestrictive.value))
            mostRestrictive = r;
        else if ((r.comparison === '>' || r.comparison === '>=') &&
            (mostRestrictive === null || mostRestrictive.value === null || r.value > mostRestrictive.value))
            mostRestrictive = r;
    }
    return mostRestrictive;
};

export const findMatchingRule = (rules: IScoreRule[], value: number | undefined) => {
    if (value === undefined) return null;
    const orderedRules = sortByAttribute(rules, 'value');
    return getMostRestrictiveRule(orderedRules, value);
};
